import React, { useEffect, useRef } from 'react';
import './splitLogin.css';

const asset = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

/* Pulsing shield with electric arcs, sat beside the "Sign In" heading. */
export const ShieldMark = () => (
  <svg className="shield-mark" viewBox="0 0 40 40" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="shieldGrad2" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="rgba(47, 181, 232, 0.30)" />
        <stop offset="100%" stopColor="rgba(14, 95, 194, 0.05)" />
      </linearGradient>
    </defs>
    <path className="arc a1" d="M8.5 12 Q4.5 9.5 6 5.5" />
    <path className="arc a2" d="M31.5 12 Q35.5 9.5 34 5.5" />
    <path className="arc a3" d="M7.5 19.5 Q2.5 19 3.5 23.5" />
    <path className="arc a4" d="M32.5 19.5 Q37.5 19 36.5 23.5" />
    <path className="arc a5" d="M10.5 26 Q6.5 29.5 8 33.5" />
    <path className="arc a6" d="M29.5 26 Q33.5 29.5 32 33.5" />
    <g className="shield-core">
      <path
        className="shield-body"
        d="M20 8 L29 11.5 V20 C29 25.5 24.8 29.4 20 31 C15.2 29.4 11 25.5 11 20 V11.5 Z"
      />
      <path className="shield-bolt" d="M21.5 12 L16 20 L19.5 20 L18 26 L24 18 L20.5 18 Z" />
    </g>
  </svg>
);

const CIRCUIT_TRACE =
  'M 560 18 L 472 18 L 450 40 L 366 40 L 344 18 L 258 18 L 232 44 L 126 44 L 104 22 L 0 22';

/* Same four pads, left → right, so sparks follow the pulse once it finishes. */
const CIRCUIT_NODES = [
  { x: 104, y: 22, slot: 0 },
  { x: 232, y: 44, slot: 1, fault: true },
  { x: 344, y: 18, slot: 2 },
  { x: 450, y: 40, slot: 3 },
];

const SPARK_BOLTS = [
  'M0 -2.2 L-0.9 -3.6 L0.6 -4.8 L-0.5 -6.4 L0.2 -8.2',
  'M1.9 -1.3 L3.3 -2 L2.5 -3.8 L4.7 -4.6 L5.9 -6.7',
  'M2.1 0.2 L3.9 -0.5 L4.7 1.1 L6.6 0.3 L8.4 1.2',
  'M1.4 1.7 L2.4 3.2 L1.3 4.2 L3.1 5.6 L2.6 7.5',
  'M-0.2 2.1 L0.7 3.7 L-0.8 4.7 L0.3 6.5 L-0.5 8.2',
  'M-1.8 1.1 L-3.2 2.2 L-2.4 3.9 L-4.6 4.7 L-5.6 6.8',
  'M-2.1 -0.1 L-4 0.5 L-4.7 -1 L-6.7 -0.2 L-8.3 -1.1',
  'M-1.5 -1.6 L-2.7 -3 L-1.5 -4.1 L-3.3 -5.4 L-2.7 -7.3',
];

const SPARK_NEEDLES = [
  'M0.8 -3.2 L1.4 -5.4',
  'M3.2 1.2 L5.5 2.1',
  'M1.1 3.4 L1.8 5.6',
  'M-3.4 0.8 L-5.6 0.2',
  'M-2.8 -2 L-4.6 -3.6',
  'M2.6 -2.4 L4.2 -4',
];

const SPARK_EMBERS = [
  [0.2, -8.2],
  [5.9, -6.7],
  [8.4, 1.2],
  [2.6, 7.5],
  [-0.5, 8.2],
  [-5.6, 6.8],
  [-8.3, -1.1],
  [-2.7, -7.3],
];

const CircuitSparks = () => (
  <g className="circuit-sparks">
    <g transform="scale(1.45)">
      <circle className="spark-bloom" cx="0" cy="0" r="5.5" />
      <circle className="spark-core" cx="0" cy="0" r="1.5" />
      <g className="spark-bolts" filter="url(#circuitSparkGlow)">
        {SPARK_BOLTS.map((d, i) => (
          <path key={d} className={`spark-bolt sb${i + 1}`} d={d} />
        ))}
        {SPARK_NEEDLES.map((d, i) => (
          <path key={d} className={`spark-needle sn${i + 1}`} d={d} />
        ))}
      </g>
      <g className="spark-embers">
        {SPARK_EMBERS.map(([cx, cy], i) => (
          <circle key={`${cx}-${cy}`} className={`spark-ember se${i + 1}`} cx={cx} cy={cy} r="0.55" />
        ))}
      </g>
    </g>
  </g>
);

const CircuitField = () => (
  <div className="circuit-field" aria-hidden="true">
    <svg className="circuit-svg" viewBox="0 0 560 70" fill="none" overflow="visible">
      <defs>
        <filter id="circuitSparkGlow" x="-160%" y="-160%" width="420%" height="420%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.7" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path className="circuit-track" d={CIRCUIT_TRACE} />
      <path className="circuit-pulse circuit-pulse-glow" d={CIRCUIT_TRACE} pathLength="100" />
      <path className="circuit-pulse circuit-pulse-core" d={CIRCUIT_TRACE} pathLength="100" />
      <g className="circuit-pads">
        {CIRCUIT_NODES.map((node) => (
          <g
            key={`${node.x}-${node.y}`}
            className={`circuit-pad-group spark-slot-${node.slot}`}
            transform={`translate(${node.x} ${node.y})`}
          >
            {node.fault ? (
              <>
                <circle className="circuit-contain" cx="0" cy="0" r="3.5" />
                <circle className="circuit-node" cx="0" cy="0" r="3.5" />
              </>
            ) : (
              <circle className="circuit-pad" cx="0" cy="0" r="3.5" />
            )}
            <CircuitSparks />
          </g>
        ))}
      </g>
    </svg>
    <p className="circuit-caption">
      <span>Safer Circuits</span>
      <span className="circuit-caption-rule" />
      <span>Smarter Tomorrow</span>
    </p>
  </div>
);

const IconGear = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" />
  </svg>
);

const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3 19 6.5V12c0 4.4-3 7.4-7 8.8C8 19.4 5 16.4 5 12V6.5L12 3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const IconImpact = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3c-2.8 3.4-7 6.2-7 11a7 7 0 0 0 14 0c0-4.8-4.2-7.6-7-11Z" />
  </svg>
);

const IconBolt = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 3 5 14h7l-1 7 8-11h-7l1-7Z" />
  </svg>
);

const IconBrain = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 8a3 3 0 1 1 2-5.2A4 4 0 0 1 16 6a3 3 0 0 1 2 5.6M8 8v2M16 8v2M7 14a3 3 0 0 0 0 6c.8 0 1.5-.3 2-.8M17 14a3 3 0 0 1 0 6c-.8 0-1.5-.3-2-.8M9 20v-6a3 3 0 0 1 6 0v6" />
  </svg>
);

const IconBuilding = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 21V7l8-4 8 4v14" />
    <path d="M9 21v-6h6v6M9 10h.01M15 10h.01M12 10h.01M9 14h.01M15 14h.01" />
  </svg>
);

const IconFactory = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M4 21V10l6 4V10l6 4V8l4-2v15" />
  </svg>
);

const IconPanel = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M4 10h16M10 10v10" />
  </svg>
);

const IconIndustry = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 21V9l5 3V9l5 3V8h6v13" />
    <path d="M8 21v-3M12 21v-3M16 21v-3" />
  </svg>
);

const HUD_CX = 210;
const HUD_CY = 210;
const HUD_TICK_COUNT = 144;

const HUD_TICKS = Array.from({ length: HUD_TICK_COUNT }, (_, i) => {
  const angle = (i / HUD_TICK_COUNT) * Math.PI * 2 - Math.PI / 2;
  const major = i % 12 === 0;
  const mid = i % 3 === 0;
  const outer = 204;
  const inner = major ? 188 : mid ? 193 : 198;
  return {
    x1: HUD_CX + Math.cos(angle) * outer,
    y1: HUD_CY + Math.sin(angle) * outer,
    x2: HUD_CX + Math.cos(angle) * inner,
    y2: HUD_CY + Math.sin(angle) * inner,
    major,
    mid,
  };
});

const DeviceHud = () => (
  <div className="device-stage" aria-hidden="true">
    <div className="orbital-halo" />

    <svg className="hud-ring hud-ring-ticks" viewBox="0 0 420 420">
      <circle cx={HUD_CX} cy={HUD_CY} r="186" fill="none" stroke="#2aa8d4" strokeWidth="0.7" opacity="0.35" />
      {HUD_TICKS.map((tick, i) => (
        <line
          key={i}
          x1={tick.x1}
          y1={tick.y1}
          x2={tick.x2}
          y2={tick.y2}
          stroke={tick.major ? '#8aeeff' : '#3cc6ea'}
          strokeWidth={tick.major ? 1.5 : tick.mid ? 1 : 0.7}
          opacity={tick.major ? 0.95 : tick.mid ? 0.7 : 0.42}
        />
      ))}
    </svg>

    <svg className="hud-ring hud-ring-glow" viewBox="0 0 420 420">
      <circle cx={HUD_CX} cy={HUD_CY} r="176" fill="none" stroke="#4fd6ff" strokeWidth="7" opacity="0.08" />
      <circle cx={HUD_CX} cy={HUD_CY} r="172" fill="none" stroke="#5ee7ff" strokeWidth="1.8" opacity="0.85" />
      <circle cx={HUD_CX} cy={HUD_CY} r="164" fill="none" stroke="#2fb5e8" strokeWidth="1" opacity="0.35" />
    </svg>

    <svg className="hud-ring hud-ring-dashed" viewBox="0 0 420 420">
      <circle cx={HUD_CX} cy={HUD_CY} r="162" fill="none" stroke="#4fd6ff" strokeWidth="1.1" strokeDasharray="3 9" opacity="0.55" />
      <circle cx={HUD_CX} cy={HUD_CY} r="152" fill="none" stroke="#2fb5e8" strokeWidth="0.8" strokeDasharray="1 14" opacity="0.4" />
    </svg>

    <svg className="hud-ring hud-ring-arcs" viewBox="0 0 420 420">
      <circle
        cx={HUD_CX}
        cy={HUD_CY}
        r="172"
        fill="none"
        stroke="#7cf0ff"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeDasharray="58 284"
        opacity="0.95"
      />
      <circle
        cx={HUD_CX}
        cy={HUD_CY}
        r="172"
        fill="none"
        stroke="#1aa3c9"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeDasharray="42 300"
        strokeDashoffset="170"
        opacity="0.7"
      />
    </svg>

    <div className="orbit orbit-one"><span /></div>
    <div className="orbit orbit-two"><span /></div>
    <div className="orbit orbit-three"><span /></div>

    <svg className="device-guides" viewBox="0 0 420 420" fill="none">
      <g>
        <path d="M36 128v-32h32M352 96h32v32M36 292v32h32M352 324h32v-32" />
        <path d="M18 210h22m340 0h22M210 18v22m0 340v22" />
      </g>
      <circle cx={HUD_CX} cy={HUD_CY} r="162" strokeDasharray="1.2 16" />
      <circle cx="52" cy="210" r="2.2" fill="#9cf4ff" stroke="none" />
      <circle cx="368" cy="210" r="2.2" fill="#9cf4ff" stroke="none" />
      <circle cx="210" cy="52" r="2.2" fill="#9cf4ff" stroke="none" />
    </svg>
    <p className="device-hud-caption device-hud-caption-top">
      <span />
      Arc Detection Sensor
      <span />
    </p>
    <p className="device-hud-caption device-hud-caption-tr">Sensor 01</p>
    <p className="device-hud-caption device-hud-caption-bl">Connected by H-Link</p>
  </div>
);

const IconWave = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12h4l2-7 4 15 3-12 2 7 2-3h3" />
  </svg>
);

const ArcSignalWaveform = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let size = { width: 1, height: 1 };
    let rafId = 0;
    let start = 0;
    let cancelled = false;

    const fitCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      size = { width, height };
    };

    const drawWave = (ms) => {
      const { width: w, height: h } = size;
      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = 'rgba(23, 103, 133, 0.2)';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      for (let x = 0; x < w; x += 27) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
      }
      for (let y = 0; y < h; y += 23) {
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();

      const center = h * 0.54;
      const t = ms / 1000;
      const rgb = '48, 212, 242';
      const padTop = Math.max(14, h * 0.22);
      const padBottom = Math.max(12, h * 0.15);
      const points = [];
      for (let x = 0; x <= w; x += 1.5) {
        const n = x / w;
        const phase = n * 17 + t * 0.38;
        const noise =
          Math.sin(phase * 27.3) * 0.14 +
          Math.sin(phase * 49.1) * 0.1 +
          Math.sin(phase * 11.5) * 0.12;
        const position = ((n + t * 0.045) % 0.32) / 0.32;
        const spike =
          Math.exp(-Math.pow((position - 0.43) / 0.032, 2)) * 1.07 -
          Math.exp(-Math.pow((position - 0.48) / 0.037, 2)) * 0.6;
        const value = noise + spike;
        const y = Math.max(padTop, Math.min(h - padBottom, center - value * h * 0.23));
        points.push([x, y]);
      }

      ctx.beginPath();
      ctx.moveTo(0, center);
      points.forEach(([x, y]) => ctx.lineTo(x, y));
      ctx.lineTo(w, center);
      ctx.closePath();
      ctx.fillStyle = `rgba(${rgb}, 0.05)`;
      ctx.fill();

      ctx.beginPath();
      points.forEach(([x, y], i) => {
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = `rgba(${rgb}, 0.94)`;
      ctx.shadowBlur = 6;
      ctx.shadowColor = `rgba(${rgb}, 0.6)`;
      ctx.lineWidth = 1.3;
      ctx.stroke();
      ctx.shadowBlur = 0;
    };

    const onResize = () => {
      fitCanvas();
      if (reduced) drawWave(0);
    };

    const tick = (now) => {
      if (cancelled) return;
      if (!start) start = now;
      drawWave(now - start);
      rafId = requestAnimationFrame(tick);
    };

    fitCanvas();
    window.addEventListener('resize', onResize, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    observer?.observe(canvas);

    if (reduced) {
      drawWave(0);
    } else {
      rafId = requestAnimationFrame(tick);
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
    };
  }, []);

  return (
    <div className="waveform-container">
      <div className="waveform-caption">
        <span>NORMALIZED SIGNAL</span>
        <IconWave />
      </div>
      <canvas
        ref={canvasRef}
        className="waveform-canvas"
        aria-label="Illustrative arc-signal waveform; simulated data, not device telemetry"
        role="img"
      />
      <div className="waveform-axis">
        <span>0 ms</span>
        <span>250</span>
        <span>500 ms</span>
      </div>
    </div>
  );
};

const ArcSignalCard = () => (
  <article className="hud-card hud-card-signal">
    <header className="hud-card-head">
      <span>Arc Signal Analysis</span>
      <span className="hud-live">
        <i />
        Live
      </span>
    </header>
    <ArcSignalWaveform />
    <ul className="hud-metrics">
      <li>
        <span className="hud-metric-label">
          <IconBolt />
          Arc Risk
        </span>
        <span className="hud-metric-value is-safe">
          <i />
          Low
        </span>
      </li>
      <li>
        <span className="hud-metric-label">
          <IconBolt />
          Voltage
        </span>
        <span className="hud-metric-value">412.3 V</span>
      </li>
      <li>
        <span className="hud-metric-label">
          <IconBolt />
          Current
        </span>
        <span className="hud-metric-value">32.6 A</span>
      </li>
      <li>
        <span className="hud-metric-label">
          <IconBolt />
          Temperature
        </span>
        <span className="hud-metric-value">34.2 °C</span>
      </li>
    </ul>
  </article>
);

const AiDetectionCard = () => (
  <article className="hud-card hud-card-ai">
    <header className="hud-card-head">
      <span className="hud-card-title-row">
        <IconBrain />
        AI-Powered Detection
      </span>
    </header>
    <ul className="hud-pills">
      {['Detect', 'Analyze', 'Alert', 'Learn', 'Prevent', 'Always On'].map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </article>
);

const FeatureCard = ({ icon, title, items }) => (
  <article className="hud-card hud-card-feature">
    <header className="hud-card-head">
      <span className="hud-card-title-row">
        {icon}
        {title}
      </span>
    </header>
    <ul className="hud-feature-list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </article>
);

const LOCATIONS = [
  { label: 'Data Center', icon: <IconBuilding /> },
  { label: 'Smart Factory', icon: <IconFactory /> },
  { label: 'Distribution Panels', icon: <IconPanel /> },
  { label: 'Industrial Facilities', icon: <IconIndustry /> },
];

const NETWORK_LINKS = [
  { id: 'link-1', d: 'M200 196Q275 34 370 114', offset: 0 },
  { id: 'link-2', d: 'M370 114Q495 -28 610 112', offset: 0.3 },
  { id: 'link-3', d: 'M610 112Q735 6 858 149', offset: 0.5 },
  { id: 'link-4', d: 'M200 196Q405 26 610 112', offset: 0.7 },
  { id: 'link-5', d: 'M370 114Q620 33 858 149', offset: 0.15 },
];

const NETWORK_NODES = [
  { x: 200, y: 196 },
  { x: 370, y: 114 },
  { x: 610, y: 112 },
  { x: 858, y: 149 },
];

const GlobeNetwork = () => {
  const svgRef = useRef(null);
  const links = NETWORK_LINKS;
  const nodes = NETWORK_NODES;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const packets = Array.from(svg.querySelectorAll('.network-packets circle')).map((node) => {
      const path = svg.querySelector(`#${node.dataset.path}`);
      if (!path || typeof path.getTotalLength !== 'function') return null;
      return {
        node,
        path,
        length: path.getTotalLength(),
        offset: Number(node.dataset.offset) || 0,
      };
    }).filter(Boolean);

    const place = (ms) => {
      packets.forEach((packet) => {
        const proportion = reduced ? packet.offset % 1 : ((ms / 6800) + packet.offset) % 1;
        const point = packet.path.getPointAtLength(proportion * packet.length);
        packet.node.setAttribute('cx', point.x.toFixed(2));
        packet.node.setAttribute('cy', point.y.toFixed(2));
      });
    };

    if (reduced) {
      place(0);
      return undefined;
    }

    let rafId = 0;
    const tick = (ms) => {
      place(ms);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <svg
      ref={svgRef}
      className="network-svg"
      viewBox="0 0 1000 205"
      preserveAspectRatio="none"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="network-stroke" x1="0" y1="1" x2="0" y2="0">
          <stop stopColor="#08bde9" stopOpacity="0.3" />
          <stop offset="1" stopColor="#7eefff" stopOpacity="0.9" />
        </linearGradient>
      </defs>
      <g className="network-paths">
        {links.map((link) => (
          <path key={link.id} id={link.id} d={link.d} />
        ))}
      </g>
      <g className="network-packets">
        {links.map((link) => (
          <circle key={link.id} data-path={link.id} data-offset={link.offset} r="2.6" />
        ))}
      </g>
      <g className="network-nodes">
        {nodes.map((node) => (
          <g key={`${node.x}-${node.y}`} transform={`translate(${node.x} ${node.y})`}>
            <ellipse rx="15" ry="5" />
            <circle r="4" />
          </g>
        ))}
      </g>
    </svg>
  );
};

function HaechieSplitLogin({ children }) {
  const scrollToSignIn = (event) => {
    event.preventDefault();
    document.getElementById('sign-in')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="split-page v2-page">
      <div className="split-left">
        <div className="split-left-bg" aria-hidden="true">
          <img src={asset('/background_final_main.png')} alt="" />
        </div>
        <div className="split-left-overlay"></div>

        <div className="hero-hud">
          <div className="hero-top">
            <div className="hero-top-row">
              <p className="split-left-tagline">Electrical Fire Safety Solution</p>
              <div className="hero-monitor-group">
                <p className="hero-monitor">Monitor · Detect · Prevent</p>
                <a href="#sign-in" className="hero-sign-in" onClick={scrollToSignIn}>
                  <svg className="hero-sign-in-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M15 4h3.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M10 16.5 14.5 12 10 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14 12H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Sign In
                </a>
              </div>
            </div>
            <h1 className="split-left-heading">
              Real-Time Protection<br /><span className="heading-space"> </span>for Every <span>Circuit</span>
            </h1>
            <p className="hero-sub">
              AI-Powered Arc Detection for a Safer, Smarter World
            </p>
          </div>

          <div className="hero-mid">
            <div className="hero-stage">
              <svg className="hud-wires hud-wires-default" viewBox="0 0 1000 400" preserveAspectRatio="none" aria-hidden="true">
                {/* Outer ring → Arc Signal Analysis (H → diagonal up → H) */}
                <path d="M350 108 H 310 L 270 68 H 230" />
                {/* Outer ring → AI-Powered Detection (same as Our Impact, left side) */}
                <path d="M350 295 H 310 L 270 336 H 230" />
                {/* Outer ring → Compact & Reliable (H → diagonal up → H) */}
                <path d="M650 105 H 690 L 730 64 H 770" />
                {/* Outer ring → Built for Real-World Use (straight from middle) */}
                <path d="M650 200 H 770" />
                {/* Outer ring → Our Impact (H → diagonal down → H) */}
                <path d="M650 295 H 690 L 730 336 H 770" />
                <circle className="hud-wire-ring" cx="350" cy="108" r="2.1" />
                <circle className="hud-wire-ring" cx="350" cy="295" r="2.1" />
                <circle className="hud-wire-ring" cx="650" cy="105" r="2.1" />
                <circle className="hud-wire-ring" cx="650" cy="200" r="2.1" />
                <circle className="hud-wire-ring" cx="650" cy="295" r="2.1" />
              </svg>
              <svg className="hud-wires hud-wires-band" viewBox="0 0 1000 400" preserveAspectRatio="none" aria-hidden="true">
                <path d="M350 108 H 318 L 284 68 H 248" />
                <path d="M350 295 H 318 L 284 336 H 248" />
                <path d="M650 105 H 682 L 716 64 H 752" />
                <path d="M650 200 H 752" />
                <path d="M650 295 H 682 L 716 336 H 752" />
                <circle className="hud-wire-ring" cx="350" cy="108" r="2.1" />
                <circle className="hud-wire-ring" cx="350" cy="295" r="2.1" />
                <circle className="hud-wire-ring" cx="650" cy="105" r="2.1" />
                <circle className="hud-wire-ring" cx="650" cy="200" r="2.1" />
                <circle className="hud-wire-ring" cx="650" cy="295" r="2.1" />
              </svg>

              <div className="hero-col hero-col-left">
                <ArcSignalCard />
                <AiDetectionCard />
              </div>

              <div className="hero-col hero-col-center">
                <p className="sensor-monitor">
                  <span>Monitor</span>
                  <span>Detect</span>
                  <span>Prevent</span>
                </p>
                <div className="split-left-product-3d">
                  <DeviceHud />
                  <img
                    src={asset('/ehaechie-device-transparent.png')}
                    alt="e-HAECHIE device"
                    className="split-left-product-img"
                  />
                </div>
              </div>

              <div className="hero-col hero-col-right">
              <FeatureCard
                icon={<IconGear />}
                title="Compact & Reliable"
                items={[
                  'High-Sensitivity Sensing',
                  'Robust Industrial Design',
                  'Easy Installation',
                ]}
              />
              <FeatureCard
                icon={<IconShield />}
                title="Built for Real-World Use"
                items={[
                  'Stable Operation',
                  'Low Maintenance',
                  'Long-Term Reliability',
                ]}
              />
              <FeatureCard
                icon={<IconImpact />}
                title="Our Impact"
                items={[
                  'Prevent Electrical Fires',
                  'Enable Operational Continuity',
                  'Support a Safer, Cleaner Future',
                ]}
              />
            </div>
            </div>
          </div>

          <div className="hero-bottom">
            <div className="hero-network">
              <GlobeNetwork />
              <ul className="hero-locations">
                {LOCATIONS.map((loc) => (
                  <li key={loc.label}>
                    <span className="location-icon">{loc.icon}</span>
                    <span className="location-label">{loc.label}</span>
                    <span className="tab-indicator" />
                  </li>
                ))}
              </ul>
            </div>
            <p className="hero-footer-line">Intelligence Today. A Safer Tomorrow.</p>
          </div>
        </div>
      </div>

      <div className="split-right" id="sign-in">
        <CircuitField />

        <div className="v3-panel">
          <div className="v3-header">
            <div className="v3-brand-row">
              <div className="v3-brand-icon">
                <img src={asset('/device_icon.png')} alt="" />
              </div>
              <div className="v3-brand-text">
                <h2 className="v3-brand-name">
                  e<span className="v3-brand-dash">-</span>HAECHIE
                </h2>
                <p className="v3-brand-sub">Arc Detection System</p>
              </div>
            </div>
          </div>

          <div className="v3-form-middle">
            {children}
          </div>

          <div className="split-footer">
            <img src={asset('/company_logo_black.png')} alt="Company Logo" className="split-footer-logo" />
            <p className="split-footer-copy">
              HL Mando Corp. ALL RIGHTS RESERVED Copyright 2025
            </p>
            <p className="split-footer-tagline">Safer · Smarter · Cleaner · Together</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HaechieSplitLogin;
